import { createSelector, createFeatureSelector } from '@ngrx/store';
import {
	quoteDetailAdapter,
	QuoteDetailsState,
	quoteDetailFeatureKey,
} from './quote-detail.reducer';
import { Job, ConfirmPrevailingWageResponse } from 'src/shared/api';
import { getAllRailroadEntities } from 'src/admin-app/railroads/railroad-detail/+state/railroad-detail.selectors';
import { getAllCustomerEntities } from 'src/admin-app/customers/customer-detail/+state/customer-detail.selectors';
import { railroadToOptionalJobFieldsToShow } from 'src/admin-app/shared/optional-fields/optional-fields.utility';
import { omit } from 'lodash';
import { hasAnyNonZeroValues } from 'src/shared/utilities/utility-functions';
import { buildListMessage } from 'src/admin-app/utilities/list-filtering/list-message';

export const getQuoteDetailState = createFeatureSelector<QuoteDetailsState>(
	quoteDetailFeatureKey
);

const { selectEntities } = quoteDetailAdapter.getSelectors();

export const getLoadingStatus = createSelector(
	getQuoteDetailState,
	(state: QuoteDetailsState) => state.isLoading
);

export const getTitle = createSelector(
	getQuoteDetailState,
	(state: QuoteDetailsState) =>
		state.selectedQuoteId > 0 ? 'Edit Quote' : 'Create Quote'
);

export const getSelectedQuoteId = createSelector(
	getQuoteDetailState,
	(state: QuoteDetailsState) => state.selectedQuoteId
);

export const getAllQuoteEntities = createSelector(
	getQuoteDetailState,
	selectEntities
);

export const getSelectedQuote = createSelector(
	getAllQuoteEntities,
	getSelectedQuoteId,
	(quoteEntities, quoteId) => quoteEntities[quoteId]
);

export const getQuoteEntity = (id: number) =>
	createSelector(getAllQuoteEntities, (entities) => entities[id]);

export const getCloseAfterSaving = createSelector(
	getQuoteDetailState,
	(state: QuoteDetailsState) => !state.shouldStayOnPage
);

export const getIsSaving = createSelector(
	getQuoteDetailState,
	(state: QuoteDetailsState) => state.isSaving
);

export const getContractRates = createSelector(
	getQuoteDetailState,
	(state: QuoteDetailsState) => state.contractedRates
);

export const getHasFieldContact = createSelector(
	getSelectedQuote,
	(quote) => quote && quote.fieldContacts && quote.fieldContacts.length > 0
);

export const getRequireJobDates = createSelector(
	getQuoteDetailState,
	(state: QuoteDetailsState) => !state.allowTbdJobDates
);

export const getCanApproveQuote = createSelector(
	getSelectedQuote,
	getQuoteDetailState,
	(quote, state) => quote && quote.id > 0 && !state.isApproving
);

export const canPrintQuoteReport = createSelector(
	getSelectedQuote,
	getQuoteDetailState,
	(quote, state) => quote && quote.id > 0 && !state.isPrintingQuoteReport
);

export const getPrintQuoteReport = createSelector(
	getSelectedQuote,
	getQuoteDetailState,
	(quote, state) => quote && quote.id > 0 && !state.isPrintingQuoteReport
);

export const getPrevailingWageConfirmation = createSelector(
	getSelectedQuote,
	(quote) => {
		let confirmation: ConfirmPrevailingWageResponse;
		if (
			quote &&
			quote.prevailingWageInfo &&
			quote.prevailingWageInfo.confirmedBy &&
			quote.prevailingWageInfo.confirmedDate
		) {
			confirmation = {
				confirmedBy: quote.prevailingWageInfo.confirmedBy,
				confirmedDate: quote.prevailingWageInfo.confirmedDate,
			};
		}
		return confirmation;
	}
);

export const getError = createSelector(
	getQuoteDetailState,
	(state: QuoteDetailsState) => state.error
);

export const getShouldSaveButtonShake = createSelector(
	getQuoteDetailState,
	(state: QuoteDetailsState) => state.error && !state.shouldStayOnPage
);

const getRailroadIdForQuote = createSelector(
	getSelectedQuote,
	getQuoteDetailState,
	(quote: Job, state: QuoteDetailsState) => {
		let railroadId = state.railroadId;
		if (!railroadId && quote && quote.railroad) {
			railroadId = quote.railroad.id;
		}

		return railroadId;
	}
);

const getCustomerIdForQuote = createSelector(
	getSelectedQuote,
	getQuoteDetailState,
	(quote: Job, state: QuoteDetailsState) => {
		let customerId = state.customerId;
		if (!customerId && quote && quote.customer) {
			customerId = quote.customer.id;
		}

		return customerId;
	}
);

export const getRailroadForQuote = createSelector(
	getAllRailroadEntities,
	getRailroadIdForQuote,
	(railroadEntities, railroadId) =>
		railroadEntities[railroadId] ? railroadEntities[railroadId] : null
);

export const getOptionalJobFields = createSelector(
	getRailroadForQuote,
	(railroad) =>
		railroad ? railroadToOptionalJobFieldsToShow(railroad) : null
);

export const hasOptionalJobFields = createSelector(
	getOptionalJobFields,
	(fields) => fields && Object.values(fields).some((f) => !!f)
);

export const getCustomerForQuote = createSelector(
	getAllCustomerEntities,
	getCustomerIdForQuote,
	(customerEntities, customerId) =>
		customerEntities[customerId] ? customerEntities[customerId] : null
);

export const getDivision = createSelector(
	getQuoteDetailState,
	(state: QuoteDetailsState) => state.division
);

export const getDivisionOptions = createSelector(
	getRailroadForQuote,
	(railroad) => (railroad ? railroad.divisions : [])
);

export const getSubdivisionOptions = createSelector(getDivision, (division) =>
	division ? division.subdivisions : []
);

export const getOfferedServiceOptions = createSelector(
	getRailroadForQuote,
	(railroad) => (railroad ? railroad.offeredServices : [])
);

export const getRegularTimeBasis = createSelector(
	getQuoteDetailState,
	(state) => state.regularTimeBasis
);

export const getOvertimeBasis = createSelector(
	getQuoteDetailState,
	(state) => state.overtimeBasis
);

export const getQuoteLogLoadingStatus = createSelector(
	getQuoteDetailState,
	(state) => !state.isQuoteLogInitialized || state.isQuoteLogLoading
);

export const getErrorApprovingQuote = createSelector(
	getQuoteDetailState,
	(state) => state.errorApprovingQuote
);

export const getQuoteLogListMessage = createSelector(
	getQuoteDetailState,
	(state) =>
		buildListMessage(
			state.error,
			state.isQuoteLogLoading,
			state.pagedQuoteLogResults
		)
);

export const getQuoteLogs = createSelector(getQuoteDetailState, (state) =>
	state.pagedQuoteLogResults ? state.pagedQuoteLogResults.results : []
);

export const getQuoteReportsDisabledMsg = createSelector(
	getSelectedQuote,
	(quote) => {
		let disabledMsg: string;
		if (
			!quote ||
			!quote.fieldContacts ||
			quote.fieldContacts.length === 0
		) {
			disabledMsg =
				'Cannot print a quote report without a saved field contact';
		}
		return disabledMsg;
	}
);

export const getApproveQuoteDisabledMsg = createSelector(
	getSelectedQuote,
	(quote) => {
		let disabledMsg: string;
		if (!quote || !hasAnyNonZeroValues(omit(quote.contractedRates, 'id'))) {
			disabledMsg =
				'Cannot approve a quote without saved contracted rates';
		}

		return disabledMsg;
	}
);

export const getIsNonBillableJob = createSelector(getSelectedQuote, (quote) =>
	quote ? quote.isNonBillable : false
);
